import React from "react";
import {
  DbExplorerStateAndActions,
  useDbExplorerStore,
} from "../../stores/dbExplorerStore";
import { useShallow } from "zustand/react/shallow";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
} from "../ui/card";
import { Database } from "lucide-react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";

interface ConnectionStringFormProps {
  onSuccess?: () => void;
  isDialogMode?: boolean;
}

export function ConnectionStringForm({
  onSuccess,
  isDialogMode = false,
}: ConnectionStringFormProps) {
  const {
    availableAgents,
    selectedAgentId,
    setSelectedAgentConnection,
    integrationsLoading,
  } = useDbExplorerStore(
    useShallow((state: DbExplorerStateAndActions) => ({
      availableAgents: state.availableAgents,
      selectedAgentId: state.selectedAgentId,
      setSelectedAgentConnection: state.setSelectedAgentConnection,
      integrationsLoading: state.integrationsLoading,
    })),
  );

  const handleAgentSelect = (agentId: string) => {
    void setSelectedAgentConnection(agentId);
    if (onSuccess) {
      onSuccess();
    }
  };

  const cardClassName = `w-full ${isDialogMode ? "" : "max-w-md mx-auto my-8"}`;

  return (
    <Card className={`${cardClassName} border-slate-200/60 bg-white/95 backdrop-blur-sm shadow-lg`}>
      {!isDialogMode && (
        <CardHeader className="space-y-3">
          <CardTitle className="text-2xl font-bold bg-gradient-to-r from-slate-900 via-slate-800 to-slate-900 bg-clip-text text-transparent">
            Connect to Database
          </CardTitle>
          <CardDescription className="text-slate-600">
            Choose one of the databases already connected through an agent integration.
          </CardDescription>
        </CardHeader>
      )}
      <CardContent className="space-y-5">
        <div className="space-y-3 rounded-md border border-slate-200/70 bg-gradient-to-br from-slate-50 to-white p-4">
          <div className="flex items-start gap-3">
            <div className="rounded-md bg-emerald-100 p-2 text-emerald-700">
              <Database className="h-4 w-4" />
            </div>
            <div className="space-y-1">
              <div className="text-sm font-semibold text-slate-800">Connected integrations</div>
              <p className="text-xs leading-5 text-slate-600">
                Explore only uses agent-managed database integrations.
              </p>
            </div>
          </div>
          <Select
            value={selectedAgentId || undefined}
            onValueChange={handleAgentSelect}
            disabled={integrationsLoading || availableAgents.length === 0}
          >
            <SelectTrigger className="border-slate-200/60 bg-white/90">
              <SelectValue
                placeholder={
                  integrationsLoading
                    ? "Loading integrations..."
                    : availableAgents.length > 0
                      ? "Select connected database"
                      : "No connected databases"
                }
              />
            </SelectTrigger>
            <SelectContent className="border-slate-200/60">
              {availableAgents.map((agent) => (
                <SelectItem key={agent.id} value={agent.id}>
                  <div className="flex min-w-0 items-center gap-3">
                    <div className="h-2 w-2 rounded-full bg-emerald-500" />
                    <div className="min-w-0">
                      <div className="truncate font-medium text-slate-800">{agent.name}</div>
                      {agent.description ? (
                        <div className="truncate text-xs text-slate-500">{agent.description}</div>
                      ) : null}
                    </div>
                  </div>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </CardContent>
    </Card>
  );
}
